import { Link } from "react-router-dom";
import type { NotificationLine } from "../lib/notifications.js";
import { severityPillClass } from "../lib/actionItems.js";
import type { ActionItemSeverity } from "../lib/actionItems.js";

/**
 * The rows of a collapsed notification — one watch evaluation that fired many
 * rows — rendered for the app rather than printed from the email.
 *
 * The email flattens each row to text with a bare URL beneath it, which mail
 * clients turn into links. In the app that same text rendered as dead link
 * text, so the title itself is the link here and no URL is ever shown.
 */
export function NotificationLines({
  lines,
  totalCount
}: {
  lines: NotificationLine[];
  totalCount: number | null;
}) {
  if (lines.length === 0) return null;
  const omitted = Math.max(0, (totalCount ?? lines.length) - lines.length);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {lines.map((line, i) => {
        const body = (
          <>
            {line.severity && (
              <span className={`pill ${severityPillClass(line.severity as ActionItemSeverity)}`} style={{ marginRight: 8 }}>
                {line.severity}
              </span>
            )}
            <span>{line.title}</span>
          </>
        );
        return (
          <div key={line.action_item_id ?? `${line.title}-${i}`} style={{ lineHeight: 1.5 }}>
            <div>
              {line.url_path ? (
                // `.adminListName` is the house treatment for a clickable
                // name — the action items list styles these same titles with
                // it. A bare <Link> is invisible here: styles.css resets every
                // anchor to inherited colour with no underline.
                <Link className="adminListName" to={line.url_path} style={{ fontSize: "inherit" }}>
                  {body}
                </Link>
              ) : (
                body
              )}
            </div>
            {line.detail && (
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                {line.detail}
              </div>
            )}
          </div>
        );
      })}
      {omitted > 0 && (
        <div className="muted" style={{ fontSize: 13 }}>
          …and {omitted} more.
        </div>
      )}
    </div>
  );
}
